import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DashboardLayout from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { getWhitePapers, deleteWhitePaper } from '../services/whitePaperService';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function WhitePaperList() {
  const [whitePapers, setWhitePapers] = useState([]);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedWhitePapers,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(whitePapers, 10);

  useEffect(() => {
    fetchWhitePapers();
  }, []);

  const fetchWhitePapers = async () => {
    try {
      const data = await getWhitePapers();
      setWhitePapers(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      console.error('Error fetching white papers:', err);
      setWhitePapers([]);
      setError(err.friendlyMessage || err?.response?.data?.message || 'Failed to load white papers.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this white paper?')) {
      return;
    }

    try {
      await deleteWhitePaper(id);
      fetchWhitePapers();
    } catch (err) {
      console.error('Error deleting white paper:', err);
      setError(err.friendlyMessage || err?.response?.data?.message || 'Failed to delete white paper.');
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <AdminPageHeader
          eyebrow="Content"
          title="White Papers"
          description="Manage downloadable thought leadership documents and their publishing details."
          meta={<Chip label={`${whitePapers.length} white papers`} variant="outlined" />}
          action={
            <Button variant="contained" onClick={() => navigate('/whitepapers/create')}>
              Create White Paper
            </Button>
          }
        />

        {error ? <Alert severity="error">{error}</Alert> : null}

        {isMobile ? (
          whitePapers.length ? (
            <Grid container spacing={2}>
              {paginatedWhitePapers.map((paper) => (
                <Grid item xs={12} key={paper.whitePaperId}>
                  <AdminSection>
                    <Stack spacing={1.5}>
                      <Typography variant="h6">{paper.title}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={paper.category || 'Uncategorized'} size="small" variant="outlined" />
                        <Chip
                          label={
                            paper.publishDate
                              ? new Date(paper.publishDate).toLocaleDateString()
                              : 'No publish date'
                          }
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={() => navigate(`/whitepapers/edit/${paper.whitePaperId}`)}>
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(paper.whitePaperId)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </AdminSection>
                </Grid>
              ))}
            </Grid>
          ) : (
            <AdminSection>
              <AdminEmptyState
                title="No white papers yet"
                description="Create the first white paper to enable gated downloads on the public site."
              />
            </AdminSection>
          )
        ) : (
          <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Publish Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {whitePapers.length ? (
                  paginatedWhitePapers.map((paper) => (
                    <TableRow key={paper.whitePaperId}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography sx={{ fontWeight: 600 }}>{paper.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {paper.slug}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{paper.category || '-'}</TableCell>
                      <TableCell>
                        {paper.publishDate ? new Date(paper.publishDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() => navigate(`/whitepapers/edit/${paper.whitePaperId}`)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(paper.whitePaperId)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <AdminEmptyState
                        title="No white papers yet"
                        description="Create the first white paper to enable gated downloads on the public site."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </AdminSection>
        )}
      </Stack>
    </DashboardLayout>
  );
}

export default WhitePaperList;
